registerComponent("sections/produtos", function (component) {
/**
 * PRODUTOS — a pilha das seis seções.
 *
 * ⚠️ Este arquivo não comuta mais abas. O painel de abas de 29/08 foi revertido
 * em 01/09: as seis seções voltaram ao empilhado do arquivo (ver
 * `decisions/0012-produtos-pilha-no-scroll.md`). E desde 09/09 elas NÃO se
 * sobrepõem mais na rolagem: o `sticky` por seção, a medida `--sec-h` que o
 * guardava e a classe `.produtos--pilha` que o ligava saíram (ver
 * `decisions/0074-as-secoes-de-produto-deixam-de-se-sobrepor.md`). As seis
 * rolam no fluxo, e nada aqui esconde conteúdo.
 *
 * O que sobrou para o JS são três coisas pequenas:
 *
 *   1. MEDIR A PILHA. A altura da ÚLTIMA seção vai para `--pilha-ultima-h`,
 *      publicada no `medir()`: é o que encurta o alcance do `sticky` da barra
 *      do submenu, que solta o topo quando essa seção chega nele (ver
 *      `margin-bottom` em _submenu.less). Um ResizeObserver remede quando uma
 *      seção muda de altura (fonte, imagem, container query).
 *
 *   2. O SUBMENU. Os links são âncora de verdade; com JS o destino é calculado
 *      pela posição de LAYOUT da seção (acumulada das alturas) e a rolagem é
 *      suave. E um scroll-spy marca com `aria-current` a seção que está no
 *      alto da pilha.
 *
 *   3. A ÂNCORA NA URL (08/09 à noite, ADR 0056). Cada seção tem um link
 *      único: clicar numa aba escreve `#produto-…` na URL (`pushState`, sem o
 *      salto nativo), o scroll-spy mantém o fragmento na seção que está no
 *      alto (`replaceState`) e o limpa fora dela — acima da primeira seção e
 *      depois da última; abrir a página por um
 *      desses links leva à seção pela régua da pilha (o salto nativo do
 *      navegador erra pelo `sticky`), e voltar/avançar no histórico
 *      (`hashchange`) rola até a seção do fragmento. Recarregar (F5) e
 *      voltar de outra página NÃO seguem o fragmento: aí quem manda é a
 *      restauração de rolagem do navegador — a página fica onde estava.
 *
 * Acessibilidade: o `tablist` foi desmontado junto com as abas. São links de
 * navegação comuns dentro de um `<nav>` — o que eles de fato são — e o item
 * atual se anuncia por `aria-current`.
 *
 * @param {HTMLElement} component - elemento raiz do componente
 * @param {Object} params
 */

const submenu = component.querySelector('.submenu');
const produtos = component.querySelector('.produtos');
const pilha = component.querySelector('.produtos__pilha');
const paineis = Array.from(component.querySelectorAll('.produtos__painel'));

if (submenu && produtos && pilha && paineis.length) {
    /* Os links do submenu, na ordem do trilho, casados com a seção pelo
       fragmento do próprio href — que é o que faz o link funcionar como âncora
       sem JS. `aba.hash` já entrega só o "#id", mesmo com o <base href> do
       _base.html tornando o href absoluto. */
    const abas = Array.from(submenu.querySelectorAll('.submenu__link'))
        .map((aba) => ({ elemento: aba, painel: component.querySelector(`#${CSS.escape(aba.hash.slice(1))}`) }))
        .filter((aba) => aba.painel);

    /* ---------------------------------------------------------------- medidas --
       `--pilha-topo` é um `calc()` de custom properties, que `getComputedStyle`
       não resolve. Mas ele também está no `scroll-margin-top` de cada seção — e
       ESSA propriedade o navegador resolve em px. É a mesma fonte, sem duplicar
       a conta aqui.

       As POSIÇÕES são acumuladas das alturas a partir do topo da pilha em
       coordenadas de página. Nasceu assim quando as seções eram `sticky` (presa
       no alto, uma seção mentia sobre onde estava); sem o `sticky` daria o mesmo
       ler cada `getBoundingClientRect`, e a conta ficou por ser uma leitura só. */
    let topo = 0;
    let posicoes = [];
    let fim = 0;          /* onde a última seção termina: dali em diante a URL perde o fragmento */

    const medir = () => {
        topo = parseFloat(getComputedStyle(paineis[0]).scrollMarginTop) || 0;

        const vao = parseFloat(getComputedStyle(pilha).rowGap) || 0;

        /* Sem o `translate` do `.produtos`: abaixo do degrau a pilha desce
           `--header-h` com a barra enquanto o header está à vista por gesto
           (ADR 0046), e uma medida feita no meio disso — o ResizeObserver
           dispara quando uma imagem carrega — deixaria todas as posições 64
           deslocadas até a próxima. */
        let y = pilha.getBoundingClientRect().top + window.scrollY - Asa.translateY(produtos);

        posicoes = paineis.map((painel) => {
            const inicio = y;

            y += painel.offsetHeight + vao;
            return inicio;
        });

        fim = y - vao;

        /* A ALTURA DA ÚLTIMA SEÇÃO, e nada além dela. É essa medida que encurta
           o alcance do `sticky` da barra, para ela soltar o topo exatamente
           quando essa seção ENCOSTA na régua da pilha (ver o `margin-bottom` em
           _submenu.less). Fica no <section> do painel, que é o ancestral comum
           da barra e do `.produtos`.

           ⚠️ O VÃO NÃO ENTRA (03/09). Somando `vao` aqui a barra soltava o topo
           4 antes, com a seção parada 4 abaixo do rodapé dela — e, como as duas
           sobem no mesmo passo daí em diante, essa fresta de 4 ficava aberta
           pelo resto da rolagem, mostrando a seção de baixo passando por dentro
           dela. O cliente apontou em 03/09. As outras cinco seções prendem
           encostadas na barra; esta agora também.

           Fora do gate de movimento de propósito: soltar não é animação, é
           layout — vale igual para quem pede menos movimento. */
        const ultima = paineis[paineis.length - 1];

        component.style.setProperty('--pilha-ultima-h', `${ultima.offsetHeight}px`);
    };

    /* --------------------------------------------------------- a âncora na URL --
       O fragmento é o `id` da seção (`#produto-conta`… em pt, pedido do cliente de 08/09 à noite; `#product-checking-account`… em en, a tradução do rótulo), o mesmo do `href` das
       abas, que é o que faz o link funcionar sem JS. Escrito por `pushState`
       no clique (uma entrada de histórico por navegação, como uma âncora
       comum) e por `replaceState` pelo scroll-spy (a URL acompanha a seção do
       alto sem encher o histórico).

       ⚠️ SEMPRE COM O `pathname` NA FRENTE. Com o `<base href>` do _base.html,
       um `pushState('#id')` seria resolvido contra a base, não contra a
       página — e numa URL fora da raiz iria para o lugar errado. O `try` é
       pelo limite do Chromium de 100 escritas por 30s (uma exceção aqui não
       pode derrubar o resto do scroll-spy). */
    const indiceDe = (hash) => paineis.findIndex((painel) => `#${painel.id}` === hash);

    const urlCom = (hash) => `${window.location.pathname}${window.location.search}${hash}`;

    const escreverHash = (indice, empilhar = false) => {
        const hash = `#${paineis[indice].id}`;

        if (window.location.hash === hash) return;

        try {
            window.history[empilhar ? 'pushState' : 'replaceState'](null, '', urlCom(hash));
        } catch (e) { /* limite de escritas do histórico */ }
    };

    /* Fora da pilha — acima dela ou depois da última seção — a URL volta a ser
       a da página; só se o fragmento era de uma seção: um `#blog-titulo` de
       outro link fica. */
    const limparHash = () => {
        if (indiceDe(window.location.hash) < 0) return;

        try {
            window.history.replaceState(null, '', urlCom(''));
        } catch (e) { /* idem */ }
    };

    /* ------------------------------------------------------------ scroll-spy --
       A seção do alto da pilha é a ÚLTIMA cujo topo de layout já passou pela
       régua. `Math.max(0, …)` porque acima da primeira nenhuma passou, e o
       destaque continua sendo o dela — mas a URL só ganha o fragmento enquanto
       a pilha está na régua (`dentro`): da primeira seção passar até a última
       terminar. Depois dos produtos (ASA Private em diante) o fragmento sai —
       pedido do cliente de 08/09 à noite. */
    let atual = -1;
    let dentro = null;

    /* Tagueamento (plano v3.1): o id da aba sai do fragmento (`#produto-conta` → `conta`). */
    const idDaAba = (aba) => ((aba.elemento.getAttribute('href') || '').split('#')[1] || '').replace(/^produto-/, '');

    const marcar = (y = window.scrollY) => {
        const limite = y + topo + 1;

        let indice = 0;

        for (let i = 0; i < posicoes.length; i += 1) {
            if (posicoes[i] <= limite) indice = i;
        }

        const agoraDentro = posicoes.length > 0 && posicoes[0] <= limite && limite < fim;

        if (indice === atual && agoraDentro === dentro) return;

        if (agoraDentro) escreverHash(indice);
        else if (dentro !== null) limparHash();

        dentro = agoraDentro;

        if (indice === atual) return;
        atual = indice;

        if (agoraDentro && abas[indice]) {
            Asa.trackUmaVez(`produto:${indice}`, 'product_tab_view', { target_id: idDaAba(abas[indice]), method: 'scroll' });
        }

        abas.forEach((aba, i) => {
            if (i === indice) aba.elemento.setAttribute('aria-current', 'true');
            else aba.elemento.removeAttribute('aria-current');
        });
    };

    /* ------------------------------------------------------------- a rolagem --
       O clique leva a seção para a régua da pilha. `App.scroll.scrollTo` já
       cuida de animar (ou não, com `prefers-reduced-motion`) e de respeitar o
       limite do documento. A URL ganha o fragmento ANTES da rolagem, para o
       histórico ter a entrada mesmo que a animação seja interrompida. */
    abas.forEach((aba, i) => {
        aba.elemento.addEventListener('click', (event) => {
            if (!posicoes.length) return;

            event.preventDefault();
            escreverHash(i, true);
            App.scroll.scrollTo(posicoes[i] - topo);

            // Abaixo do desktop a barra é um seletor (a lista do `_submenu.js`).
            const metodo = Asa.desktop && !Asa.desktop.matches ? 'select' : 'click';

            Asa.track('product_tab_click', { target_id: idDaAba(aba), method: metodo, index: i });
        });
    });

    /* ---------------------------------------------- chegando por um link único --
       A página abriu com o fragmento de uma seção. O navegador já saltou para a seção pelo
       `scroll-margin-top` dela, mas antes das imagens: a conta da régua é
       refeita aqui, sem animação, e repetida a cada remedida
       (o ResizeObserver, abaixo) enquanto a página carrega — até o `load` ou
       o primeiro gesto de quem está vendo, o que vier antes.

       SÓ NUMA NAVEGAÇÃO NOVA. Recarregar (F5) e voltar de outra página trazem
       o mesmo fragmento, mas aí o navegador RESTAURA a rolagem de antes, e é
       isso que tem de valer: a pessoa fica onde estava (pedido do cliente de
       08/09). Antes o `Navigation.on('./')` do main.js rolava para o topo em
       todo carregamento — ver o comentário de lá. */
    const navegacao = /** @type {PerformanceNavigationTiming | undefined} */ (
        window.performance && performance.getEntriesByType
            ? performance.getEntriesByType('navigation')[0]
            : undefined
    );
    const restaurando = Boolean(navegacao && (navegacao.type === 'reload' || navegacao.type === 'back_forward'));

    let ancora = restaurando ? -1 : indiceDe(window.location.hash);

    const irParaAncora = () => {
        if (ancora < 0 || !posicoes.length) return;

        window.scrollTo({ top: posicoes[ancora] - topo, behavior: /** @type {ScrollBehavior} */ ('instant') });
    };

    const soltarAncora = () => {
        ancora = -1;
    };

    if (ancora >= 0) {
        // Depois do `load` a página é da pessoa; um quadro a mais para a última
        // remedida do `load` ainda alinhar.
        window.addEventListener('load', () => requestAnimationFrame(soltarAncora), { once: true });

        for (const gesto of ['wheel', 'touchstart', 'keydown', 'pointerdown']) {
            window.addEventListener(gesto, soltarAncora, { once: true, passive: true });
        }
    }

    /* Voltar e avançar no histórico entre as entradas das abas — ou um link de
       fora da barra para uma seção — rolam até ela pela régua da pilha. O
       `pushState`/`replaceState` daqui não disparam `hashchange`, então isto
       não reage ao próprio scroll-spy. */
    window.addEventListener('hashchange', () => {
        const i = indiceDe(window.location.hash);

        if (i < 0 || !posicoes.length) return;

        App.scroll.scrollTo(posicoes[i] - topo);
    });

    window.addEventListener('resize', () => {
        medir();
        atual = -1;      /* as posições mudaram: o destaque de antes não vale */
        marcar();
    }, { passive: true });

    medir();
    irParaAncora();

    /* Registra por último: `Asa.aoRolar` chama `marcar` de imediato, e as
       posições precisam estar medidas antes — e a âncora já lida da URL, porque
       `marcar` a reescreve. O rAF é o mesmo do header, do hero e do submenu
       (ver `aoRolar` em assets/.scripts/asa.js). */
    Asa.aoRolar(marcar);

    /* ------------------------------------------------------------- remedir --
       A altura de uma seção mexe na posição de todas as seguintes — e, se for
       a última, no alcance do `sticky` da barra. O ResizeObserver dispensa
       remedir na mão: fonte que chega depois, imagem que carrega, `container
       query` que reflui — tudo passa por ele.

       Fora do gate de movimento de propósito (09/09): medir é layout, e vale
       igual para quem pede menos movimento. Até o ADR 0074 este bloco também
       publicava `--sec-h` por seção e ligava `.produtos--pilha` — o `sticky`
       que fazia as seções se sobreporem; saiu tudo com ele. */
    if (window.ResizeObserver) {
        const observador = new ResizeObserver(() => {
            medir();
            irParaAncora();
            atual = -1;
            marcar();
        });

        paineis.forEach((painel) => observador.observe(painel));
    }
}

/* ══════════════════════════════════════════ a entrada das seções na rolagem ═══
   Cada seção de produto revela o conteúdo dela quando entra na tela. É o MESMO
   padrão de entrada do hero — estado final na base do CSS, "antes" só sob
   `html.js-anim` enquanto falta `.is-in`, movimento por `transition` — com o
   gatilho trocado de carregamento para viewport. Quem faz isso é
   `Asa.revelarAoEntrar`, ao lado de `Asa.revelar` em assets/.scripts/asa.js;
   duração, curva, distâncias e escalonamento moram no CSS (ver "A ENTRADA" em
   _produtos.less).

   A ORDEM é a da espinha que as seis seções compartilham: o título, o apoio do
   cabeçalho e depois os blocos da grade — cartões e mídia — na sequência do
   documento. `querySelectorAll` já devolve nessa ordem, então não há lista
   escrita à mão para sair de sincronia quando uma seção mudar de composição.

   ── DOIS MODOS, E O DEGRAU DECIDE (05/09) ──────────────────────────────────
   No DESKTOP a seção chega inteira na tela e as peças entram numa LINHA só; no
   CELULAR cada peça entra sozinha quando chega a vez. Essa lógica — e o rearme
   quando o degrau muda — nasceu aqui e subiu para `Asa.armarEntrada` em 08/09
   (ADR 0051), quando ASA Private, Sobre a Asa, Seja cliente e Central de
   Conteúdos passaram a fazer a mesma entrada; lá está o porquê de cada modo.

   Fora do bloco acima de propósito: a entrada não depende do submenu nem da
   pilha, e tem de valer mesmo se o `sticky` não for ligado. */
/* `.entrada` entrou na lista em 09/09. O seletor cobre as peças de quem usa a
   ESPINHA da pilha (cabeçalho de seção, cartão, cartão do app, mídia), e é o
   que faz as seis seções da home e cinco das seis de "Para sua Empresa"
   entrarem sem marcação no HTML. Risco Sacado é a exceção: ela não usa a grade
   de `sections/produtos` (texto à esquerda, mockup à direita, três passos
   embaixo), então nenhuma daquelas classes aparece nela e a seção NÃO ANIMAVA —
   nem antes nem depois do ADR 0051.
   Em vez de listar as classes dela aqui (uma lista escrita à mão por seção, que
   é justamente o que o ADR 0051 descartou), a pilha passa a aceitar a MESMA
   marcação por classe que as quatro seções de baixo usam. A folha delas
   (`assets/.styles/_entrada.less`) é global e já traz a transição e o "antes",
   então uma peça marcada aqui herda a coreografia sem regra nova. */
Asa.armarEntrada(component.querySelectorAll('.produtos__painel'), [
    '.section-head__title',
    '.section-head__aside',
    '.card',
    '.produtos__app',
    '.produtos__midia',
    '.entrada',
].join(','));

/* ═════════════════════════════════════════════ o parallax dentro das seções ═══
   Camadas marcadas com `data-parallax` derivam na vertical enquanto a seção
   cruza a tela. Mesmo vocabulário do hero — o fator mora no HTML, junto da
   camada a que pertence — e mesma disciplina: escreve em `translate`, que é
   propriedade própria, para não disputar o `transform` que a animação de
   entrada usa nos mesmos cartões.

   ⚠️ A REFERÊNCIA NÃO É `scrollY`, e é aqui que este parallax difere do do hero.
   O que se usa é a posição da SEÇÃO na janela: `p` vai de +1 (a seção chegando
   por baixo) a −1 (saindo por cima), e vale 0 quando o centro dela está no
   centro da tela. Nasceu assim porque as seções eram `sticky` (presa, uma
   seção parava de andar e um deslocamento tirado de `scrollY` viraria fuga;
   ADR 0074 tirou o `sticky`), e fica: cada seção faz o percurso inteiro da
   deriva enquanto cruza a tela, independentemente de onde ela está na página.

   ⚠️ E o `p` é DA SEÇÃO, não de cada camada (05/09). Medido em cada camada, duas
   peças em alturas diferentes têm progressos diferentes no mesmo instante e
   cruzam o zero em momentos distintos — uma sobe enquanto a outra ainda desce.
   Com um `p` só por seção elas andam sempre para o mesmo lado, e o que as separa
   é apenas a intensidade do fator. É assim que a profundidade se lê como
   profundidade, e não como duas peças se repelindo.

   Um quadro por rajada, pelo `Asa.aoRolar` que header, hero e submenu já
   compartilham, e a escrita só acontece quando o valor muda em 0,1px. */
{
    /* O curso total em px de quem tem fator 1. Pequeno de propósito: com os
       fatores de 0,08 e 0,12 dos widgets de Investimentos, a deriva de cada um
       fica em ~45 e ~67px de ponta a ponta. */
    const CURSO = 280;

    const secoes = Array.from(component.querySelectorAll('.produtos__painel'))
        .map((secao) => ({
            elemento: secao,
            camadas: Array.from(secao.querySelectorAll('[data-parallax]'))
                .map((camada) => ({
                    elemento: camada,
                    fator: parseFloat(camada.dataset.parallax) || 0,
                    escrito: '',
                }))
                .filter((camada) => camada.fator),
        }))
        .filter((secao) => secao.camadas.length);

    if (secoes.length && Asa.movimentoPermitido()) {
        Asa.aoRolar(() => {
            const meio = window.innerHeight / 2;

            for (const secao of secoes) {
                const r = secao.elemento.getBoundingClientRect();

                /* +1 entrando por baixo, 0 no centro, −1 saindo por cima. */
                const p = Math.max(-1, Math.min(1, (r.top + r.height / 2 - meio) / meio));

                for (const camada of secao.camadas) {
                    const d = Math.round(p * camada.fator * CURSO * 10) / 10;
                    const valor = d ? `0 ${d}px` : '';

                    if (valor === camada.escrito) continue;

                    camada.escrito = valor;
                    camada.elemento.style.translate = valor;
                }
            }
        });
    }
}

});
